import React from 'react';
import { Box } from '@mantine/core';
import { IconGripHorizontal } from '@tabler/icons-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RecipeCard } from './RecipeCard';
import { MealType } from '../../types/mealPlan';
import { MealPlan, MealPlanAssignment, Recipe, SubstitutionGenerationProgress } from '../../types/mealPlan';
import { HouseholdSegment } from '../../utils/householdSegment';

// Subtle grip that sits astride the top-center border of the card. Shared between
// the live sortable card and the DragOverlay clone so the handle stays visible
// while the card is picked up. On the live card it carries the dnd-kit activator
// ref/listeners; on the overlay it's purely visual.
export const MealCardDragHandle = React.forwardRef<
  HTMLDivElement,
  { grabbing?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ grabbing, style, ...rest }, ref) => (
  <Box
    ref={ref}
    aria-label="Drag to reorder"
    style={{
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 16,
      borderRadius: 8,
      backgroundColor: 'var(--mantine-color-body)',
      border: '1px solid var(--mantine-color-default-border)',
      color: 'var(--mantine-color-dimmed)',
      cursor: grabbing ? 'grabbing' : 'grab',
      // Let dnd-kit own touches on the handle (prevents the page from scrolling
      // when the user grabs it) without affecting the rest of the card.
      touchAction: 'none',
      userSelect: 'none',
      ...style,
    }}
    {...rest}
  >
    <IconGripHorizontal size={14} />
  </Box>
));
MealCardDragHandle.displayName = 'MealCardDragHandle';

interface SortableCustomMealCardProps {
  id: string;
  dragDisabled?: boolean;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
}

// Sortable shell for custom meal cards: same grip, transform, and drag
// styling as SortableMealCard, with the card itself supplied by the caller
// ("let's move taco night to Friday" is as normal as reordering recipes).
export const SortableCustomMealCard: React.FC<SortableCustomMealCardProps> = ({
  id,
  dragDisabled,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: dragDisabled });

  return (
    <Box
      ref={setNodeRef}
      h="100%"
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 200ms ease',
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
      }}
    >
      {children(
        dragDisabled ? null : (
          <MealCardDragHandle
            ref={setActivatorNodeRef}
            grabbing={isDragging}
            {...attributes}
            {...listeners}
          />
        )
      )}
    </Box>
  );
};

interface SortableMealCardProps {
  id: string;
  assignment: MealPlanAssignment;
  isOwner: boolean;
  mealPlan: MealPlan;
  loadingActivation: boolean;
  menuOpen: Record<string, boolean>;
  openTooltip: string | null;
  servingsInputMode: string | null;
  tempServings: number;
  savingDays: Set<string>;
  substitutingDays: Set<string>;
  substitutionProgressByKey?: Record<string, SubstitutionGenerationProgress>;
  swappingDays: Set<string>;
  ratingDays: Set<string>;
  dragDisabled?: boolean;
  handleAssignmentClick: (assignment: MealPlanAssignment) => void;
  handleToggleSaveRecipe: (recipeId: string) => void;
  handleTooltipClick: (day: string, e: React.MouseEvent) => void;
  handleSubstituteWithInstructions: (day: string) => void;
  handleServingsInputMode: (day: string, servings: number) => void;
  handleSubmitServings: (day: string) => void;
  handleAssignmentFeedback: (assignment: MealPlanAssignment) => void;
  handleRateUp: (assignment: MealPlanAssignment) => void;
  handleRateDown: (assignment: MealPlanAssignment) => void;
  handleUndoRating: (assignment: MealPlanAssignment) => void;
  handleDeleteAssignment: (assignment: MealPlanAssignment) => void;
  handleSwapAlternative: (assignment: MealPlanAssignment, recipe: Recipe, swapSequence: number, targetRecipeIdRef: React.RefObject<string>) => Promise<void>;
  setMenuOpen: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setServingsInputMode: React.Dispatch<React.SetStateAction<string | null>>;
  setTempServings: React.Dispatch<React.SetStateAction<number>>;
  setSelectedRecipeForNutrition: React.Dispatch<React.SetStateAction<Recipe | null>>;
  setNutritionDrawerOpened: React.Dispatch<React.SetStateAction<boolean>>;
  isFirstCard?: boolean;
  userDietaryRestrictions?: string[] | null;
  userHouseholdSegment?: HouseholdSegment;
  radius?: 'sm' | 'md' | 'lg' | 'xl';
  pendingSwapCallbacksRef?: React.RefObject<Map<string, () => Promise<void>>>;
  cookDayPreviews?: Record<string, Recipe>;
  onCookDayPreview?: (
    plannedDate: string,
    mealType: MealType,
    recipe: Recipe | null
  ) => void;
  onFlipChange?: (flippedRecipeId: string | null) => void;
}

export const SortableMealCard: React.FC<SortableMealCardProps> = ({
  id,
  assignment,
  dragDisabled,
  ...recipeCardProps
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: dragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      h="100%"
      style={{
        ...style,
        position: 'relative',
      }}
    >
      {/* Card content keeps full pointer events: the whole card can be swiped to
          change the carousel and the page can be scrolled freely over it.
          Reordering is initiated only from the grip handle, which is rendered on
          the card's front face (so it flips with the card) and is the only thing
          that starts a drag - vertical scrolling never triggers an accidental one. */}
      <RecipeCard
        assignment={assignment}
        {...recipeCardProps}
        dragHandle={
          dragDisabled ? undefined : (
            <MealCardDragHandle
              ref={setActivatorNodeRef}
              grabbing={isDragging}
              {...attributes}
              {...listeners}
            />
          )
        }
      />
    </Box>
  );
};
